// The frame of every signed-in screen: the sidebar (logo, the permitted NAV nodes grouped by job,
// Help last), the top bar (breadcrumb; environment chip, who is signed in with email and role,
// theme, sign out) and the content. Below 1100px the sidebar becomes a drawer behind Menu.
import type { ComponentChildren } from "preact";
import { createContext } from "preact";
import { useContext, useEffect, useState } from "preact/hooks";
import type { NavEntry } from "../router/router";
import { navSections } from "../router/router";
import type { NavId } from "../router/route";
import type { Me } from "../session/session";
import type { Crumb } from "./breadcrumb";
import type { EnvironmentChip } from "./environment";
import { Icon, Link, applyTheme, browserStorage, effectiveScheme, readTheme, toggleTheme, toggleView, writeTheme, type ThemeChoice } from "@nexus/ui";
import { userLines } from "./identity";
import { Lockup, LogoMark } from "./logo";

export { Link } from "@nexus/ui";

/** Lets a screen name where it is ("Hooli / Billing") after the nav entry in the breadcrumb. */
export const BreadcrumbContext = createContext<((tail: readonly Crumb[] | null) => void) | null>(null);

/** Set the breadcrumb's tail while this screen is mounted; pass a stable key of what it shows. */
export function useBreadcrumb(tail: readonly Crumb[] | null): void {
  const set = useContext(BreadcrumbContext);
  const key = tail === null ? "" : JSON.stringify(tail);
  useEffect(() => {
    if (!set) return;
    set(tail);
    return () => set(null);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [set, key]);
}

const DARK_QUERY = "(prefers-color-scheme: dark)";

/** Whether the OS asks for dark, kept live as the OS setting changes. */
function useOsDark(): boolean {
  const [osDark, setOsDark] = useState(() => window.matchMedia?.(DARK_QUERY).matches ?? false);
  useEffect(() => {
    const query = window.matchMedia?.(DARK_QUERY);
    if (!query) return;
    const onChange = () => setOsDark(query.matches);
    query.addEventListener("change", onChange);
    return () => query.removeEventListener("change", onChange);
  }, []);
  return osDark;
}

/** One button: a moon in light (switch to dark), a sun in dark (switch to light). */
function ThemeToggle() {
  const osDark = useOsDark();
  const [choice, setChoice] = useState<ThemeChoice>(() => readTheme(browserStorage()));
  const view = toggleView(effectiveScheme(choice, osDark));
  const toggle = () => {
    const next = toggleTheme(choice, osDark);
    setChoice(next);
    writeTheme(browserStorage(), next);
    applyTheme(document.documentElement, next);
  };
  return (
    <button type="button" class="icon-button theme-toggle" aria-label={view.label} title={view.label} onClick={toggle}>
      <Icon name={view.icon} />
    </button>
  );
}

export interface ShellProps {
  me: Me;
  nav: readonly NavEntry[];
  currentNav: NavId | null;
  trail: readonly Crumb[];
  environment: EnvironmentChip | null;
  onSignOut(): void;
  children: ComponentChildren;
}

export function Shell({ me, nav, currentNav, trail, environment, onSignOut, children }: ShellProps) {
  const [navOpen, setNavOpen] = useState(false);
  const user = userLines(me);
  const sections = navSections(nav);
  const home = nav[0]?.href ?? "/";

  useEffect(() => {
    if (!navOpen) return;
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") setNavOpen(false);
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [navOpen]);

  const close = () => setNavOpen(false);

  return (
    <div class="app" data-nav-open={navOpen ? "true" : "false"}>
      <a class="skip-link" href="#content">
        Skip to content
      </a>
      <nav class="sidebar" id="sidebar" aria-label="Main">
        <Link href={home} class="brand" onNavigate={close} aria-label="Nexus by AllCode admin, overview">
          <LogoMark />
          <span class="brand-text">
            <Lockup />
            <span class="brand-sub">{environment ? `Admin · ${environment.label}` : "Admin"}</span>
          </span>
        </Link>
        {sections.map((section, index) => (
          <div key={section.label ?? `section-${index}`} class="nav-group">
            {section.label && (
              <div class="nav-heading" id={`nav-heading-${index}`}>
                {section.label}
              </div>
            )}
            <ul class="nav-section" aria-labelledby={section.label ? `nav-heading-${index}` : undefined}>
              {section.entries.map((entry) => (
                <li key={entry.nav}>
                  <Link href={entry.href} class="nav-link" current={entry.nav === currentNav} onNavigate={close}>
                    {entry.label}
                  </Link>
                </li>
              ))}
            </ul>
          </div>
        ))}
      </nav>
      {navOpen && <button type="button" class="sidebar-scrim" aria-label="Close menu" onClick={close} />}
      <div class="main-column">
        <header class="topbar">
          <button
            type="button"
            class="icon-button menu-button"
            aria-label="Menu"
            aria-expanded={navOpen}
            aria-controls="sidebar"
            onClick={() => setNavOpen((open) => !open)}
          >
            <Icon name="menu" />
          </button>
          <nav class="breadcrumb" aria-label="Breadcrumb">
            <ol>
              {trail.map((crumb, index) => (
                <li key={`${index}-${crumb.label}`}>
                  {crumb.href ? (
                    <Link href={crumb.href}>{crumb.label}</Link>
                  ) : index === trail.length - 1 ? (
                    <span aria-current="page">{crumb.label}</span>
                  ) : (
                    <span>{crumb.label}</span>
                  )}
                </li>
              ))}
            </ol>
          </nav>
          <div class="topbar-right">
            {environment && (
              <span class={`chip env-chip-${environment.tone}`} title="The environment this admin app runs in">
                {environment.label}
              </span>
            )}
            <div class="user-block">
              <span class="user-name" title={user.name}>
                {user.name}
              </span>
              <span class="user-meta" title={user.meta}>
                {user.meta}
              </span>
            </div>
            <ThemeToggle />
            <button type="button" class="button button-sm" onClick={onSignOut}>
              Sign out
            </button>
          </div>
        </header>
        <main id="content" class="content" tabIndex={-1}>
          {children}
        </main>
      </div>
    </div>
  );
}
